import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import usePermissions, { resolvePermissionRole } from 'src/configs/auth';
import Permissions from 'src/configs/auth/permissions';
import { ROLE_FINANCE_MANAGER } from 'src/constants/auth';
import { ROUTES } from 'src/constants/routes';
import { getAuth, logoutFromUser } from 'src/redux/slices/auth';

import useDefaultPage from './useDefaultPage';

const routeMap = [
  ROUTES.DEALS,
  ROUTES.INVENTORY,
  ROUTES.CONTACTS,
  ROUTES.ANALYTICS,
  ROUTES.REPORTS,
  ROUTES.COSTS,
  ROUTES.ACCOUNT,
  ROUTES.ACCOUNTS,
  ROUTES.API_KEYS,
  ROUTES.USERS,
  ROUTES.MAKES,
  ROUTES.PROFILE,
  ROUTES.ACTIVITY,
];

const useLinks = () => {
  const { t } = useTranslation();
  const { hasPermission, hasAccountPermission, canSeeActivity } = usePermissions();
  const navigate = useNavigate();
  const auth = useSelector(getAuth);
  const { getDefaultPage } = useDefaultPage();
  const [current, setCurrent] = useState(getDefaultPage());
  const dispatch = useDispatch();

  const getCurrentRoute = () => {
    const pathname = location.pathname;

    if (pathname.startsWith('/vehicle/')) {
      return ROUTES.INVENTORY;
    }

    if (pathname.startsWith(ROUTES.REPORTS)) {
      return ROUTES.REPORTS;
    }

    const matchedRoute = routeMap.find((route) => pathname.startsWith(route));

    return matchedRoute || getDefaultPage();
  };

  useEffect(() => {
    setCurrent(getCurrentRoute());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const links: any[] = [];
  const mobileLinks: any[] = [];

  const isFinanceManager = resolvePermissionRole(auth) === ROLE_FINANCE_MANAGER;

  const activityItem = {
    key: ROUTES.ACTIVITY,
    onClick: () => {
      navigate(ROUTES.ACTIVITY);
      setCurrent(ROUTES.ACTIVITY);
    },
    label: t('layout:activity'),
  };

  if (auth.currentUser) {
    // 1. Deals
    if (hasAccountPermission('deals')) {
      const dealsItem = {
        key: ROUTES.DEALS,
        onClick: () => {
          navigate(ROUTES.DEALS);
          setCurrent(ROUTES.DEALS);
        },
        label: t('layout:deals'),
      };
      links.push(dealsItem);
      mobileLinks.push(dealsItem);
    }

    // 2. Inventory
    const inventoryItem = {
      key: ROUTES.INVENTORY,
      onClick: () => {
        navigate(ROUTES.INVENTORY);
        setCurrent(ROUTES.INVENTORY);
      },
      label: t('layout:inventory'),
    };
    links.push(inventoryItem);
    mobileLinks.push(inventoryItem);

    if (!isFinanceManager) {
      const costsItem = {
        key: ROUTES.COSTS,
        onClick: () => {
          navigate(ROUTES.COSTS);
          setCurrent(ROUTES.COSTS);
        },
        label: t('layout:costs'),
      };
      links.push(costsItem);
      mobileLinks.push(costsItem);

      // 4. Reports (with submenu for desktop, flat for mobile)
      links.push({
        key: 'reports-submenu',
        label: t('layout:reports'),
        children: [
          {
            key: ROUTES.REPORTS,
            onClick: () => {
              navigate(ROUTES.REPORTS);
              setCurrent(ROUTES.REPORTS);
            },
            label: t('layout:deals'),
          },
          {
            key: ROUTES.ANALYTICS,
            onClick: () => {
              navigate(ROUTES.ANALYTICS);
              setCurrent(ROUTES.ANALYTICS);
            },
            label: t('layout:sites'),
          },
        ],
      });

      // Mobile: Reports parent item (non-clickable)
      mobileLinks.push({
        key: 'reports-mobile',
        label: t('layout:reports'),
        disabled: true,
      });
      // Mobile: flat items with indentation
      mobileLinks.push({
        key: ROUTES.REPORTS,
        onClick: () => {
          navigate(ROUTES.REPORTS);
          setCurrent(ROUTES.REPORTS);
        },
        label: t('layout:deals'),
        className: 'indented-menu-item',
      });
      mobileLinks.push({
        key: ROUTES.ANALYTICS,
        onClick: () => {
          navigate(ROUTES.ANALYTICS);
          setCurrent(ROUTES.ANALYTICS);
        },
        label: t('layout:sites'),
        className: 'indented-menu-item',
      });
    }

    // 5. Contacts
    if (hasAccountPermission('contacts') && !isFinanceManager) {
      const contactsItem = {
        key: ROUTES.CONTACTS,
        onClick: () => {
          navigate(ROUTES.CONTACTS);
          setCurrent(ROUTES.CONTACTS);
        },
        label: t('layout:contacts'),
      };
      links.push(contactsItem);
      mobileLinks.push(contactsItem);
    }

    // 6. Dealer
    if (hasPermission(Permissions.ACCESS_SCREEN_ACCOUNT)) {
      const accountItem = {
        key: ROUTES.ACCOUNT,
        onClick: () => {
          navigate(ROUTES.ACCOUNT);
          setCurrent(ROUTES.ACCOUNT);
        },
        label: t('layout:account'),
      };
      links.push(accountItem);
      mobileLinks.push(accountItem);
    }

    if (canSeeActivity) {
      links.push(activityItem);
      mobileLinks.push(activityItem);
    }
  } else {
    if (auth.superAdminToken) {
      links.push({
        key: ROUTES.ACCOUNTS,
        onClick: () => {
          navigate(ROUTES.ACCOUNTS);
        },
        label: t('layout:accounts'),
      });

      links.push({
        key: ROUTES.API_KEYS,
        onClick: () => {
          navigate(ROUTES.API_KEYS);
        },
        label: t('layout:api_keys'),
      });

      links.push({
        key: ROUTES.USERS,
        onClick: () => {
          dispatch(logoutFromUser());
          navigate(ROUTES.USERS);
        },
        label: t('layout:users'),
      });

      links.push({
        key: ROUTES.MAKES,
        onClick: () => {
          navigate(ROUTES.MAKES);
        },
        label: t('layout:makes_and_models'),
      });

      if (canSeeActivity) {
        links.push(activityItem);
        mobileLinks.push(activityItem);
      }
    }
  }

  return {
    links,
    mobileLinks,
    current,
  };
};

export default useLinks;
